import React, { useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    LoadingContext,
    M_StateActions,
    MHeader,
    MButton,
    NavigateContext,
    BufferService,
    MInputTextField,
    MSelect,
} from "src/M_Components";
import "../pages.css";
import { isFailurePayload, apiMessage } from "src/components/utils/apiResponse";
import DynamicRemoteSelectBox from "src/components/Forms/DynamicRemoteSelectBox";
import TrackingEventsTable from "src/components/Tracking/TrackingEventsTable";
import TrackingLiveMap from "src/components/Tracking/TrackingLiveMap";
import TrackingContainersTable from "src/components/Tracking/TrackingContainersTable";
import {
    validateTrackingReference,
    isValidContainerNumber,
    isValidBookingOrBlNumber,
    isValidAwb,
    sanitizeTrackingReference,
} from "src/components/utils/trackingValidation";

/**
 * TrackingConfigCrud
 *
 * Provides Create and View experiences for tracking shipments.
 *
 * Responsibilities:
 * - Render the shipment creation form.
 * - Render the shipment view form and associated visualization areas.
 * - Load provider capability information used by the Create flow.
 * - Load shipment details and provider tracking payloads used by the View flow.
 * - Persist lightweight tab state so navigation between tabs restores the UI context.
 *
 * Persistence strategy:
 * - Lightweight state such as form values, selected provider, selected mode and selected inner tab
 *   is persisted in shared state.
 * - Heavy provider tracking payloads such as events, route metadata and GeoJSON are intentionally
 *   kept only in local component state to avoid large snapshot writes and unnecessary re-renders.
 *
 * View strategy:
 * - The form mixes internal shipment data and external tracking data.
 * - Internal shipment data is used for stable business-identification fields such as provider,
 *   transport mode, tracking reference, internal reference and created date.
 * - External tracking data is used for live tracking fields such as status, current location,
 *   destination and schedule-like information.
 *
 * @param {Object} props - Component props.
 * @param {Function} props.handleTabClose - Callback used to close the current tab after a successful create.
 * @param {Function} props.toastMessages - Callback used to show feedback messages to the user.
 * @returns {JSX.Element} Tracking create/view page.
 */
export default function TrackingConfigCrud({ handleTabClose, toastMessages }) {
    const dispatch = useDispatch();
    const M_State = useSelector((state) => state.StateKeyValue);
    const { t, i18n } = useTranslation();
    const ctx = useContext(LoadingContext);
    const { renameTab } = useContext(NavigateContext);

    /* =========================================================================
     * One-shot globals coming from parent table
     * ========================================================================= */

    /**
     * selectedTab identifies the active tab instance.
     * This value is also used as the key for snapshot persistence.
     */
    const selectedTab = M_State.find((o) => o.key === "selectedTab")?.value;

    /**
     * globalOperation and globalLine are transient values pushed by the parent screen
     * when opening this component.
     *
     * These values are consumed once during bootstrap and then removed from shared state.
     */
    const globalOperation = M_State.find((obj) => obj.key === "operation")?.value;
    const globalLine = M_State.find((obj) => obj.key === "TrackingShipmentLine")?.value;

    /* =========================================================================
     * Local state: identity, operation, and data holders
     * ========================================================================= */

    /**
     * id stores the current tab id used for snapshot persistence and restoration.
     */
    const [id, setId] = useState(selectedTab);

    /**
     * operation controls the active page mode.
     * Expected values:
     * - "create"
     * - "view"
     */
    const [operation, setOperation] = useState(globalOperation ?? "Error");

    /**
     * shipment stores the internal shipment DTO returned by TrackingShipments endpoints.
     * This object is the internal source used by the View form for stable business fields.
     */
    const [shipment, setShipment] = useState(globalLine ?? {});

    /**
     * shipmentId is extracted once for convenience and used by refresh logic.
     */
    const shipmentId = shipment?.Id;

    /**
     * trackingDetails stores the heavy provider tracking payload.
     *
     * Important:
     * - This state is intentionally local only.
     * - This state is never persisted in the tab snapshot because it may contain large route,
     *   events and metadata payloads.
     */
    const [trackingDetails, setTrackingDetails] = useState(null);

    /* Create form state */

    /**
     * providers stores the list of allowed tracking providers for the current user/context.
     */
    const [providers, setProviders] = useState([]);

    /**
     * providerCode stores the currently selected provider code in Create mode.
     */
    const [providerCode, setProviderCode] = useState("");

    /**
     * mode stores the selected transport mode in Create mode.
     * Expected values:
     * - "ocean"
     * - "air"
     */
    const [mode, setMode] = useState("ocean");

    /**
     * trackingReference stores the user input that represents the shipment tracking reference.
     * Depending on mode, this may represent:
     * - Container number
     * - BL / Booking
     * - AWB
     */
    const [trackingReference, setTrackingReference] = useState("");

    /**
     * internalReference stores the tenant-defined, human-readable shipment name/reference.
     */
    const [internalReference, setInternalReference] = useState("");

    /**
     * carrierOrAirline stores the selected carrier or airline code, depending on the selected mode.
     */
    const [carrierOrAirline, setCarrierOrAirline] = useState("");

    /* Provider capability lists */

    /**
     * carrierProviders stores the providers that support the carrier lookup surface.
     */
    const [carrierProviders, setCarrierProviders] = useState([]);

    /**
     * airlineProviders stores the providers that support the airline lookup surface.
     */
    const [airlineProviders, setAirlineProviders] = useState([]);

    /**
     * opLower normalizes the operation to simplify comparisons.
     */
    const opLower = String(operation ?? "").trim().toLowerCase();

    /**
     * Flags used to simplify branching in render and side effects.
     */
    const isCreateMode = opLower === "create";
    const isViewMode = opLower === "view";

    /**
     * viewTab controls the active visualization block inside View mode.
     *
     * Supported values:
     * - "movements"
     * - "containers"
     * - "live"
     */
    const [viewTab, setViewTab] = useState("movements");

    /**
     * bootDoneRef prevents the initial bootstrap effect from running more than once
     * for the same mounted instance.
     */
    const bootDoneRef = useRef(false);

    /* Backend services */

    /**
     * Service used to load the providers allowed for tracking operations.
     */
    const svcAllowedProviders = useMemo(
        () => new BufferService("Tracking/allowed-providers", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );

    /**
     * Service used to load internal shipment records.
     */
    const svcShipments = useMemo(() => new BufferService("TrackingShipments", ctx.DEFAULTURL), [ctx.DEFAULTURL]);

    /**
     * Service used to load providers allowed for carrier lookup.
     */
    const svcCarriersAllowedProviders = useMemo(
        () => new BufferService("TrackingCarriers/allowed-providers", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );

    /**
     * Service used to load providers allowed for airline lookup.
     */
    const svcAirlinesAllowedProviders = useMemo(
        () => new BufferService("TrackingAirlines/allowed-providers", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );

    /* =========================================================================
     * Helpers: snapshot read/write
     * ========================================================================= */

    /**
     * Returns the persisted snapshot for a given tab id.
     *
     * The snapshot structure is an array of key/value pairs stored in shared state.
     *
     * @param {string} tabId - Tab identifier used as snapshot key.
     * @returns {Array<{key:string,value:any}>|null} Snapshot array or null when unavailable.
     */
    const getSnapshot = useCallback(
        (tabId) => {
            const entry = M_State.find((o) => o.key === tabId)?.value;
            return Array.isArray(entry) ? entry : null;
        },
        [M_State]
    );

    /**
     * Reads one specific value from a persisted snapshot.
     *
     * This helper avoids duplicating snapshot parsing logic during tab restoration.
     *
     * @param {string} tabId - Tab identifier used as snapshot key.
     * @param {string} key - Snapshot item key to retrieve.
     * @param {any} fallback - Fallback value returned when key does not exist.
     * @returns {any} Snapshot value or fallback.
     */
    const getSnapshotValue = useCallback(
        (tabId, key, fallback = null) => {
            const data = getSnapshot(tabId);
            if (!data?.length) return fallback;
            return data.find((x) => x.key === key)?.value ?? fallback;
        },
        [getSnapshot]
    );

    /**
     * Restores the lightweight UI state for the provided tab id.
     *
     * Important:
     * - Only lightweight state is restored here.
     * - Heavy provider tracking payloads are intentionally excluded.
     * - View mode will re-fetch tracking data after restoration when required.
     *
     * @param {string} tabId - Tab identifier to restore.
     * @returns {boolean} True when a snapshot existed and was restored; otherwise false.
     */
    const restoreSnapshot = useCallback(
        (tabId) => {
            const data = getSnapshot(tabId);
            if (!data?.length) return false;

            const pick = (k) => data.find((x) => x.key === k)?.value;

            setOperation(pick("operation") ?? "Error");
            setShipment(pick("shipment") ?? {});

            setProviders(pick("providers") ?? []);
            setProviderCode(pick("providerCode") ?? "");
            setMode(pick("mode") ?? "ocean");
            setTrackingReference(pick("trackingReference") ?? "");
            setInternalReference(pick("internalReference") ?? "");
            setCarrierOrAirline(pick("carrierOrAirline") ?? "");

            setCarrierProviders(pick("carrierProviders") ?? []);
            setAirlineProviders(pick("airlineProviders") ?? []);

            setViewTab(pick("viewTab") ?? "movements");

            return true;
        },
        [getSnapshot]
    );

    /**
     * Persists a lightweight snapshot whenever relevant UI state changes.
     *
     * Tracking payloads are intentionally excluded because:
     * - they can be large;
     * - they cause expensive shared-state writes;
     * - they can increase rendering cost when navigating between tabs.
     */
    useEffect(() => {
        if (!id) return;

        const snapshot = [
            { key: "operation", value: operation },
            { key: "shipment", value: shipment },

            { key: "providers", value: providers },
            { key: "providerCode", value: providerCode },
            { key: "mode", value: mode },
            { key: "trackingReference", value: trackingReference },
            { key: "internalReference", value: internalReference },
            { key: "carrierOrAirline", value: carrierOrAirline },

            { key: "carrierProviders", value: carrierProviders },
            { key: "airlineProviders", value: airlineProviders },

            { key: "viewTab", value: viewTab },
        ];

        dispatch(M_StateActions.addM_State({ key: id, value: snapshot }));
    }, [
        id,
        operation,
        shipment,
        providers,
        providerCode,
        mode,
        trackingReference,
        internalReference,
        carrierOrAirline,
        carrierProviders,
        airlineProviders,
        viewTab,
        dispatch,
    ]);

    /* =========================================================================
     * Tab label update
     * ========================================================================= */

    /**
     * Keeps the tab label synchronized with the current page mode and active shipment.
     *
     * Behavior:
     * - In Create mode, the generic tracking label is used.
     * - In View mode, the label is enriched with the shipment tracking reference when available.
     */
    useEffect(() => {
        if (!selectedTab || !operation) return;

        const base = isCreateMode ? t("MenuTrackingLabel") : `${t("MenuTrackingLabel")} - ${t("View")}`;
        const suffix = isViewMode && shipment?.TrackingReference ? ` "${shipment.TrackingReference}"` : "";
        renameTab(String(selectedTab), `${base}${suffix}`);
    }, [
        selectedTab,
        operation,
        i18n.language,
        shipment?.TrackingReference,
        isCreateMode,
        isViewMode,
        renameTab,
        t,
    ]);

    /* =========================================================================
     * Capability providers + bootstrap functions
     * ========================================================================= */

    /**
     * Loads the provider capability lists used by the Create form.
     *
     * Responsibilities:
     * - Load providers that support carrier lookup.
     * - Load providers that support airline lookup.
     * - Fail safely by resetting both lists when any request fails unexpectedly.
     *
     * @returns {Promise<void>} Promise representing the asynchronous operation.
     */
    const fetchCapabilityProviders = async () => {
        try {
            const [carP, airP] = await Promise.all([
                svcCarriersAllowedProviders.filter(""),
                svcAirlinesAllowedProviders.filter(""),
            ]);

            if (!isFailurePayload(carP) && Array.isArray(carP)) setCarrierProviders(carP);
            else setCarrierProviders([]);

            if (!isFailurePayload(airP) && Array.isArray(airP)) setAirlineProviders(airP);
            else setAirlineProviders([]);
        } catch {
            setCarrierProviders([]);
            setAirlineProviders([]);
        }
    };

    /**
     * Loads the tracking providers allowed for the current user/context.
     *
     * Behavior:
     * - On success, stores the provider list.
     * - If no provider is selected yet, selects the first provider by default.
     * - On failure, clears the provider list and shows a toast message.
     *
     * @returns {Promise<void>} Promise representing the asynchronous operation.
     */
    const fetchAllowedTrackingProviders = async () => {
        try {
            const res = await svcAllowedProviders.filter("");
            if (isFailurePayload(res)) {
                toastMessages("error", "", apiMessage(res, t("ErrorLoadingData")));
                setProviders([]);
                return;
            }

            const list = Array.isArray(res) ? res : [];
            setProviders(list);

            if (!providerCode && list.length) {
                setProviderCode(String(list[0]?.Code ?? ""));
            }
        } catch (err) {
            toastMessages("error", "", apiMessage(err, t("ErrorLoadingData")));
            setProviders([]);
        }
    };

    /**
     * Loads the internal shipment record and then loads the external provider tracking payload.
     *
     * Flow:
     * 1. Request the internal shipment by id.
     * 2. Use internal shipment provider and external shipment id to request tracking details.
     * 3. Store internal shipment and external tracking payload in local state.
     *
     * Notes:
     * - Internal shipment data is the source used for business-identification fields in the form.
     * - External tracking payload is the source used for live operational fields and visualizations.
     *
     * @param {number|string} idToGet - Internal shipment identifier.
     * @returns {Promise<void>} Promise representing the asynchronous operation.
     */
    const fetchShipmentAndTracking = async (idToGet) => {
        ctx.setIsLoad(true);
        try {
            // Load the internal shipment first because it provides
            // the provider code and external shipment id needed to retrieve tracking.
            const resShipment = await svcShipments.getId(idToGet);
            if (isFailurePayload(resShipment)) {
                toastMessages("error", "", apiMessage(resShipment, t("ErrorLoadingData")));
                return;
            }

            setShipment(resShipment || {});

            // The tracking endpoint requires the provider code and the provider shipment id.
            const prov = String(resShipment?.Provider ?? "").trim();
            const ext = String(resShipment?.ExternalShipmentId ?? "").trim();

            // If these identifiers are missing, tracking cannot be requested.
            if (!prov || !ext) {
                setTrackingDetails(null);
                return;
            }

            const svcTracking = new BufferService(`Tracking/id/${prov}/${ext}`, ctx.DEFAULTURL);
            const resTracking = await svcTracking.filter("");

            if (isFailurePayload(resTracking)) {
                toastMessages("error", "", apiMessage(resTracking, t("ErrorLoadingData")));
                setTrackingDetails(null);
                return;
            }

            setTrackingDetails(resTracking);
        } catch (err) {
            toastMessages("error", "", apiMessage(err, t("ErrorLoadingData")));
            setTrackingDetails(null);
        } finally {
            ctx.setIsLoad(false);
        }
    };

    /**
     * Executes the initial bootstrap for this component when no snapshot exists.
     *
     * Flow:
     * - Load capability lists first because they are needed by Create mode.
     * - If in Create mode, load allowed providers.
     * - If in View mode, load shipment and tracking payload.
     *
     * @returns {Promise<void>} Promise representing the asynchronous operation.
     */
    const bootstrapFromGlobals = async () => {
        ctx.setIsLoad(true);
        try {
            const op = globalOperation ?? operation;
            setOperation(op);

            await fetchCapabilityProviders();

            if (String(op || "").toLowerCase() === "create") {
                await fetchAllowedTrackingProviders();
                return;
            }

            if (String(op || "").toLowerCase() === "view") {
                const line = globalLine ?? shipment;
                if (line?.Id) {
                    await fetchShipmentAndTracking(line.Id);
                }
            }
        } finally {
            ctx.setIsLoad(false);
        }
    };

    /* =========================================================================
     * Boot sequence
     * ========================================================================= */

    /**
     * Initial bootstrap effect for the current tab.
     *
     * Behavior:
     * - Restore snapshot if available.
     * - When restoring a View tab, fetch the heavy tracking payload again.
     * - Otherwise bootstrap from the one-shot globals.
     *
     * Important:
     * - The heavy tracking payload is intentionally re-fetched after restoration because
     *   it is no longer persisted in the tab snapshot.
     */
    useEffect(() => {
        if (!id || bootDoneRef.current) return;

        bootDoneRef.current = true;
        ctx.setIsLoad(true);

        const restored = restoreSnapshot(id);

        // Consume one-shot bootstrap values only once.
        if (globalOperation !== undefined) dispatch(M_StateActions.removeM_State({ key: "operation" }));
        if (globalLine !== undefined) dispatch(M_StateActions.removeM_State({ key: "TrackingShipmentLine" }));

        if (restored) {
            const restoredOperation = String(getSnapshotValue(id, "operation", "") ?? "").toLowerCase();
            const restoredShipment = getSnapshotValue(id, "shipment", {});

            // View mode requires the heavy provider payload to be reloaded after restoration.
            if (restoredOperation === "view" && restoredShipment?.Id) {
                fetchShipmentAndTracking(restoredShipment.Id).finally(() => ctx.setIsLoad(false));
            } else {
                ctx.setIsLoad(false);
            }

            return;
        }

        bootstrapFromGlobals().finally(() => ctx.setIsLoad(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    /**
     * Reacts to selected tab changes.
     *
     * Behavior:
     * - Restore the lightweight snapshot for the new tab when available.
     * - Re-fetch heavy tracking payload when the restored tab is a View tab.
     * - Otherwise, bootstrap from globals when no snapshot exists.
     */
    useEffect(() => {
        const newId = M_State.find((obj) => obj.key === "selectedTab")?.value;
        if (newId && newId !== id) {
            setId(newId);
            ctx.setIsLoad(true);

            const restored = restoreSnapshot(newId);

            if (!restored) {
                bootstrapFromGlobals().finally(() => ctx.setIsLoad(false));
            } else {
                const restoredOperation = String(getSnapshotValue(newId, "operation", "") ?? "").toLowerCase();
                const restoredShipment = getSnapshotValue(newId, "shipment", {});

                if (restoredOperation === "view" && restoredShipment?.Id) {
                    fetchShipmentAndTracking(restoredShipment.Id).finally(() => ctx.setIsLoad(false));
                } else {
                    ctx.setIsLoad(false);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [M_State]);

    /* =========================================================================
     * View helpers and heuristics
     * ========================================================================= */

    /**
     * Returns the primary tracking record used by the View form and related visualizations.
     *
     * Some providers return arrays for tracking responses. In such cases, the first element
     * is treated as the primary record for the form header information and map.
     *
     * @returns {Object|null} Primary tracking record or null.
     */
    const trackingFirst = useMemo(() => {
        if (!trackingDetails) return null;
        return Array.isArray(trackingDetails) ? trackingDetails[0] : null;
    }, [trackingDetails]);

    /**
     * Memoized tracking reference passed to the live map.
     *
     * This keeps the prop reference stable and reduces unnecessary map remount pressure.
     */
    const trackingForLiveMap = useMemo(() => trackingFirst, [trackingFirst]);

    /**
     * Extracts the CO2 emission value from tracking metadata when available.
     *
     * @returns {number|null} Numeric CO2 emission value or null.
     */
    const co2Tons = useMemo(() => {
        const v = trackingFirst?.Metadata?.co2_emission ?? trackingFirst?.metadata?.co2_emission;
        if (v == null) return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    }, [trackingFirst]);

    /**
     * Formats the internal shipment created date for display in the View form.
     *
     * @returns {string} Formatted date string or empty string.
     */
    const createdAtDisplay = useMemo(() => {
        const raw = shipment?.CreatedAt;
        if (raw == null) return "";
        return fmtDateTime(raw);
    }, [shipment?.CreatedAt]);

    /**
     * Computes the schedule field displayed in the View form.
     *
     * Output:
     * - null when no schedule-like field can be derived
     * - { label, value } for one of:
     *   - ETD
     *   - ETA
     *   - Arrived At
     *
     * Rules:
     * - Ocean and air use different heuristics.
     * - Tracking metadata and events are combined to derive the best display field.
     * - Transport mode prefers the external tracking payload, but falls back to the internal shipment mode.
     *
     * @returns {{label:string,value:string}|null} Schedule field object or null.
     */
    const scheduleField = useMemo(() => {
        if (!trackingFirst) return null;

        const meta = trackingFirst?.Metadata ?? trackingFirst?.metadata ?? {};
        const modeLower = String(trackingFirst?.TransportMode ?? shipment?.Mode ?? "").trim().toLowerCase();
        const statusLower = String(trackingFirst?.Status ?? "").trim().toLowerCase();

        const metaEtd = meta?.date_of_loading || meta?.date_of_loading_initial || null;
        const metaEta = meta?.date_of_discharge || meta?.date_of_discharge_initial || null;
        const metaArrivedLike = meta?.arrived_at || meta?.arrival_time || meta?.arrival_at || null;

        const now = Date.now();

        /**
         * Formats a schedule-like field only when a valid timestamp exists.
         *
         * @param {string} label - Display label.
         * @param {string|null} iso - ISO timestamp candidate.
         * @returns {{label:string,value:string}|null} Formatted field or null.
         */
        const pick = (label, iso) => {
            if (!iso) return null;
            const formatted = fmtDateTime(iso);
            if (!formatted) return null;
            return { label, value: formatted };
        };

        if (modeLower === "ocean") {
            const events = Array.isArray(trackingFirst?.Events) ? trackingFirst.Events : [];

            // Actual arrival in the past or present is the strongest signal for "Arrived At".
            const arrivedActual = pickBestEventTimestamp(events, ["ARRV", "ARR"], {
                requireNotEstimated: true,
                requirePastOrNow: true,
            });

            // Actual departure is used as evidence that the shipment is already in transit.
            const departedActual = pickBestEventTimestamp(events, ["DEPA", "DEP"], {
                requireNotEstimated: true,
            });

            const statusLooksCompleted =
                statusLower.includes("arriv") ||
                statusLower.includes("discharg") ||
                statusLower.includes("deliver") ||
                statusLower.includes("empty_return");

            // Completed or clearly arrived shipments should prefer Arrived At.
            if (arrivedActual?.iso || statusLooksCompleted) {
                return (
                    pick(t("ArrivedAt") || "Arrived At", metaArrivedLike) ||
                    pick(t("ArrivedAt") || "Arrived At", arrivedActual?.iso) ||
                    pick(t("ArrivedAt") || "Arrived At", metaEta) ||
                    pick(t("ETA") || "ETA", metaEta) ||
                    pick(t("ETD") || "ETD", metaEtd)
                );
            }

            // In-transit shipments should prefer ETA and then ETD.
            if (departedActual?.iso) {
                return pick(t("ETA") || "ETA", metaEta) || pick(t("ETD") || "ETD", metaEtd);
            }

            // Pre-departure shipments should prefer ETD and then ETA.
            return pick(t("ETD") || "ETD", metaEtd) || pick(t("ETA") || "ETA", metaEta);
        }

        if (modeLower === "air") {
            const events = Array.isArray(trackingFirst?.Events) ? trackingFirst.Events : [];

            const arrivedActual = pickBestEventTimestamp(events, ["ARR", "ARRV"], {
                requireNotEstimated: true,
                requirePastOrNow: true,
            });
            if (arrivedActual?.iso) {
                return { label: t("ArrivedAt") || "Arrived At", value: fmtDateTime(arrivedActual.iso) };
            }

            const arrivedAny = pickBestEventTimestamp(events, ["ARR", "ARRV"], {});
            if (arrivedAny?.iso) {
                const ts = safeTime(arrivedAny.iso);
                const isFuture = ts > now;

                if (arrivedAny.estimated || isFuture || statusLower === "booked") {
                    return { label: t("ETA") || "ETA", value: fmtDateTime(arrivedAny.iso) };
                }

                return { label: t("ArrivedAt") || "Arrived At", value: fmtDateTime(arrivedAny.iso) };
            }

            const departedAny = pickBestEventTimestamp(events, ["DEP", "DEPA"], {});
            if (departedAny?.iso) {
                return { label: t("ETD") || "ETD", value: fmtDateTime(departedAny.iso) };
            }

            return null;
        }

        return null;
    }, [trackingFirst, shipment?.Mode, t]);

    /**
     * Returns the events that should be shown in the movements table.
     *
     * Rules:
     * - Air: show all events.
     * - Ocean: keep only events that have vessel information to align with the expected
     *   shipment movement semantics in the UI.
     *
     * @returns {Array} Filtered movement events.
     */
    const eventsForTable = useMemo(() => {
        const all = Array.isArray(trackingFirst?.Events) ? trackingFirst.Events : [];
        if (!all.length) return [];

        const modeLower = String(trackingFirst?.TransportMode ?? shipment?.Mode ?? "").trim().toLowerCase();
        if (modeLower === "air") return all;

        return all.filter((e) => {
            const vesselName = e?.Metadata?.vessel_name ?? e?.metadata?.vessel_name ?? null;
            const vesselImo = e?.Metadata?.vessel_imo ?? e?.metadata?.vessel_imo ?? null;
            return Boolean(String(vesselName ?? "").trim() || String(vesselImo ?? "").trim());
        });
    }, [trackingFirst, shipment?.Mode]);

    /**
     * Determines whether the current tracking payload contains movement information.
     *
     * Detection sources:
     * - Filtered events list
     * - GeoJSON features
     * - GeoJSON feature count metadata
     *
     * @returns {boolean} True when movement information exists; otherwise false.
     */
    const hasMovements = useMemo(() => {
        if (!trackingFirst) return false;

        if (eventsForTable.length > 0) return true;

        const features =
            trackingFirst?.Metadata?.route_geojson?.features ??
            trackingFirst?.metadata?.route_geojson?.features ??
            trackingFirst?.RouteGeoJson?.features ??
            trackingFirst?.route_geojson?.features ??
            null;

        if (Array.isArray(features) && features.length > 0) return true;

        const count =
            trackingFirst?.Metadata?.route_geojson_features_count ??
            trackingFirst?.metadata?.route_geojson_features_count ??
            null;

        if (Number.isFinite(Number(count)) && Number(count) > 0) return true;

        return false;
    }, [trackingFirst, eventsForTable]);

    /**
     * Determines whether the current payload supports the containers tab.
     *
     * Rules:
     * - Tracking payload must be an array.
     * - At least one record must expose a non-empty ExternalId.
     * - Transport mode must be ocean.
     *
     * @returns {boolean} True when container rows exist; otherwise false.
     */
    const hasContainers = useMemo(() => {
        const arr = Array.isArray(trackingDetails) ? trackingDetails : [];
        if (!arr.length) return false;

        const anyContainer = arr.some((x) => String(x?.ExternalId ?? "").trim().length > 0);
        if (!anyContainer) return false;

        const modeLower = String(trackingFirst?.TransportMode ?? shipment?.Mode ?? "").trim().toLowerCase();
        return modeLower === "ocean";
    }, [trackingDetails, trackingFirst?.TransportMode, shipment?.Mode]);

    /**
     * Ensures the selected inner tab remains valid when the underlying data changes.
     *
     * Example:
     * - If the user is on the containers tab and the refreshed payload no longer supports
     *   container rows, the selected tab falls back to movements.
     */
    useEffect(() => {
        if (!isViewMode) return;
        if (viewTab === "containers" && !hasContainers) setViewTab("movements");
    }, [isViewMode, viewTab, hasContainers]);

    /* =========================================================================
     * Provider capability rules for Create form
     * ========================================================================= */

    /**
     * Determines whether the selected provider supports carrier lookup.
     *
     * @returns {boolean} True when carrier lookup is supported for the selected provider.
     */
    const supportsCarriersForProvider = useMemo(() => {
        if (!providerCode) return false;
        return carrierProviders.some((p) => String(p?.Code) === String(providerCode));
    }, [carrierProviders, providerCode]);

    /**
     * Determines whether the selected provider supports airline lookup.
     *
     * @returns {boolean} True when airline lookup is supported for the selected provider.
     */
    const supportsAirlinesForProvider = useMemo(() => {
        if (!providerCode) return false;
        return airlineProviders.some((p) => String(p?.Code) === String(providerCode));
    }, [airlineProviders, providerCode]);

    /**
     * Returns the fixed set of transport mode options for the Create form.
     *
     * The mode selector is always visible. Provider capability only controls whether
     * carrier or airline selectors are rendered for the currently selected mode.
     */
    const modeOptions = useMemo(() => {
        return [
            { value: "ocean", label: "Ocean" },
            { value: "air", label: "Air" },
        ];
    }, []);

    /**
     * Clears the selected carrier or airline whenever provider or mode changes in Create mode.
     *
     * This avoids carrying a stale selection across incompatible provider/mode combinations.
     */
    useEffect(() => {
        if (!isCreateMode) return;
        setCarrierOrAirline("");
    }, [providerCode, mode, isCreateMode]);

    /**
     * Maps allowed providers into the select option shape expected by MSelect.
     *
     * @returns {Array<{value:string,label:string,raw:any}>} Provider select options.
     */
    const providerOptions = useMemo(() => {
        return (providers || []).map((p) => ({
            value: String(p?.Code ?? ""),
            label: String(p?.Name ?? p?.Code ?? ""),
            raw: p,
        }));
    }, [providers]);

    /* =========================================================================
     * Create validation + create request
     * ========================================================================= */

    /**
     * Validates the Create form input and returns the normalized tracking reference.
     *
     * Validation rules:
     * - Provider must be selected.
     * - Air requires a valid AWB.
     * - Ocean requires a valid container number or BL / booking reference.
     *
     * @returns {{ok:boolean,message?:string,normalized?:string}} Validation result object.
     */
    const validateCreateInput = () => {
        const sanitized = sanitizeTrackingReference(trackingReference);
        const res = validateTrackingReference(sanitized);

        if (!providerCode) return { ok: false, message: t("ErrorLoadingData") };

        if (String(mode).toLowerCase() === "air") {
            if (!isValidAwb(sanitized)) {
                return { ok: false, message: t("InvalidAwbFormat") };
            }
            return { ok: true, normalized: res.normalized };
        }

        if (String(mode).toLowerCase() === "ocean") {
            const okOcean = isValidContainerNumber(sanitized) || isValidBookingOrBlNumber(sanitized);
            if (!okOcean) {
                return { ok: false, message: t("InvalidOceanTrackingFormat") };
            }
            return { ok: true, normalized: sanitized };
        }

        return { ok: false, message: t("ErrorLoadingData") };
    };

    /**
     * Executes the Create shipment flow.
     *
     * Flow:
     * 1. Validate the form input.
     * 2. Build the provider-facing payload.
     * 3. Execute the create request.
     * 4. Show success or failure feedback.
     * 5. Close the current tab after a successful create.
     *
     * @returns {Promise<void>} Promise representing the asynchronous operation.
     */
    const onCreate = async () => {
        const validation = validateCreateInput();
        if (!validation.ok) {
            toastMessages("error", "", validation.message || t("ErrorSaving"));
            return;
        }

        ctx.setIsLoad(true);
        try {
            const svcCreate = new BufferService(`Tracking/${providerCode}`, ctx.DEFAULTURL);

            const payload = {
                mode: String(mode).toLowerCase(),
                externalId: validation.normalized,
                internalReference: internalReference || null,
                carrierOrAirline: carrierOrAirline || null,
                bookingNumber: String(mode).toLowerCase() === "ocean" ? validation.normalized : null,
            };

            const res = await svcCreate.post(payload);

            if (isFailurePayload(res)) {
                toastMessages("error", "", apiMessage(res, t("ErrorSaving")));
                return;
            }

            toastMessages("success", "", t("Success"));
            handleTabClose?.(id);
        } catch (err) {
            toastMessages("error", "", apiMessage(err, t("ErrorSaving")));
        } finally {
            ctx.setIsLoad(false);
        }
    };

    /**
     * Refreshes the current page according to the active mode.
     *
     * Behavior:
     * - Create mode reloads provider capability information and allowed providers.
     * - View mode reloads the internal shipment and the external tracking payload.
     *
     * @returns {Promise<void>} Promise representing the asynchronous operation.
     */
    const onRefresh = async () => {
        if (isCreateMode) {
            await fetchCapabilityProviders();
            await fetchAllowedTrackingProviders();
            return;
        }

        if (isViewMode && shipmentId) {
            await fetchShipmentAndTracking(shipmentId);
        }
    };

    /* =========================================================================
     * View header buttons
     * ========================================================================= */

    /**
     * Builds the header buttons used in View mode for switching between inner visualization tabs.
     *
     * Available buttons:
     * - Movements
     * - Containers, only when container rows are available
     * - Live
     *
     * @returns {React.ReactNode[]|null} Array of button elements or null outside View mode.
     */
    const viewHeaderButtons = useMemo(() => {
        if (!isViewMode) return null;

        /**
         * Creates a standardized header button for a specific inner tab.
         *
         * @param {string} key - Inner tab key.
         * @param {string} label - Button label.
         * @param {React.ReactNode} icon - Button icon.
         * @returns {JSX.Element} Header button.
         */
        const mkBtn = (key, label, icon) => (
            <MButton
                key={key}
                tooltip={label}
                label={label}
                icon={icon}
                backgroundColor={"var(--color-white)"}
                labelColor={viewTab === key ? "var(--color-green)" : "var(--color-grey)"}
                onClick={() => setViewTab(key)}
                hoverEffect
            />
        );

        const btns = [mkBtn("movements", t("MovementsTable") || "Movements", <i className="pi pi-list" />)];

        if (hasContainers) {
            btns.push(mkBtn("containers", t("ContainersTable") || "Containers", <i className="pi pi-box" />));
        }

        btns.push(mkBtn("live", t("LivePosition") || "Live Position", <i className="pi pi-map-marker" />));

        return btns;
    }, [isViewMode, hasContainers, viewTab, t]);

    /* =========================================================================
     * Render
     * ========================================================================= */

    return (
        <>
            <MHeader
                element={
                    <>
                        <MButton
                            tooltip={t("RefreshTable")}
                            icon={<i className="pi pi-refresh iconTable"></i>}
                            onClick={onRefresh}
                            label={t("RefreshTable")}
                        />

                        {isCreateMode && (
                            <MButton
                                tooltip={t("Save")}
                                icon={<i className="pi pi-save iconTable"></i>}
                                onClick={onCreate}
                                label={t("Save")}
                            />
                        )}

                        {isViewMode && (
                            <div style={{ display: "flex", gap: 10, marginLeft: 12, flexWrap: "wrap" }}>
                                {viewHeaderButtons}
                            </div>
                        )}
                    </>
                }
            />

            <div style={{ padding: "20px", background: "var(--color-white)" }}>
                <div className="firstDivContainer" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    <div className="alignInputsUserForm">
                        {isCreateMode && (
                            <>
                                <span className="dynamic-form-title" style={{ gridColumn: "1 / -1" }}>
                                    {t("InfoTracking")}
                                </span>

                                <MSelect
                                    label={t("Provider")}
                                    autocomplete
                                    required
                                    disabled={false}
                                    options={providerOptions}
                                    value={providerOptions.find((o) => String(o.value) === String(providerCode)) || null}
                                    onChangeValue={(sel) => {
                                        const next = String(sel?.value ?? "");
                                        setProviderCode(next);
                                        setCarrierOrAirline("");
                                    }}
                                />

                                <MSelect
                                    label={t("Mode")}
                                    autocomplete
                                    required
                                    disabled={false}
                                    options={modeOptions}
                                    value={modeOptions.find((o) => String(o.value) === String(mode)) || null}
                                    onChangeValue={(sel) => {
                                        const next = String(sel?.value ?? "ocean");
                                        setMode(next);
                                        setCarrierOrAirline("");
                                    }}
                                />

                                <MInputTextField
                                    label={t("TrackingReference")}
                                    value={trackingReference}
                                    onChange={(e) => setTrackingReference(e?.target?.value ?? "")}
                                    required
                                    disabled={false}
                                />

                                <MInputTextField
                                    label={t("InternalReference")}
                                    value={internalReference}
                                    onChange={(e) => setInternalReference(e?.target?.value ?? "")}
                                    disabled={false}
                                />

                                {String(mode).toLowerCase() === "ocean" && supportsCarriersForProvider && (
                                    <DynamicRemoteSelectBox
                                        label={t("Carrier")}
                                        serviceName="TrackingCarriers"
                                        valueId={carrierOrAirline || null}
                                        required={false}
                                        disabled={false}
                                        take={25}
                                        initialQuery=""
                                        buildQueryString={({ q, skip, take }) => {
                                            const prov = encodeURIComponent(String(providerCode || ""));
                                            const name = encodeURIComponent(String(q || ""));
                                            const s = encodeURIComponent(String(skip ?? 0));
                                            const tke = encodeURIComponent(String(take ?? 25));
                                            return `?providerCode=${prov}&name=${name}&skip=${s}&take=${tke}`;
                                        }}
                                        getItemsFromResponse={(res) => res?.carriers || []}
                                        getMoreFromResponse={(res) => res?.meta?.more === true}
                                        mapItemToOption={(r) => ({
                                            value: String(r?.scac ?? ""),
                                            label: `${String(r?.name ?? "")} (${String(r?.scac ?? "")})`,
                                            raw: r,
                                        })}
                                        onChange={(sel) => setCarrierOrAirline(String(sel?.value ?? ""))}
                                    />
                                )}

                                {String(mode).toLowerCase() === "air" && supportsAirlinesForProvider && (
                                    <DynamicRemoteSelectBox
                                        label={t("Airline")}
                                        serviceName="TrackingAirlines"
                                        valueId={carrierOrAirline || null}
                                        required={false}
                                        disabled={false}
                                        take={25}
                                        initialQuery=""
                                        buildQueryString={({ q, skip, take }) => {
                                            const prov = encodeURIComponent(String(providerCode || ""));
                                            const needle = encodeURIComponent(`contains:${String(q || "")}`);
                                            const s = encodeURIComponent(String(skip ?? 0));
                                            const tke = encodeURIComponent(String(take ?? 25));
                                            return `?providerCode=${prov}&filters%5Bname%5D=${needle}&skip=${s}&take=${tke}`;
                                        }}
                                        getItemsFromResponse={(res) => res?.airlines || []}
                                        getMoreFromResponse={(res) => res?.meta?.more === true}
                                        mapItemToOption={(r) => ({
                                            value: String(r?.iata ?? ""),
                                            label: `${String(r?.name ?? "")} (${String(r?.iata ?? "")})`,
                                            raw: r,
                                        })}
                                        onChange={(sel) => setCarrierOrAirline(String(sel?.value ?? ""))}
                                    />
                                )}
                            </>
                        )}

                        {isViewMode && (
                            <>
                                <span className="dynamic-form-title" style={{ gridColumn: "1 / -1" }}>
                                    {t("InfoTracking")}
                                </span>

                                <MInputTextField label={t("Provider")} value={shipment?.Provider ?? ""} disabled />
                                <MInputTextField label={t("TransportMode")} value={shipment?.Mode ?? ""} disabled />
                                <MInputTextField label={t("TrackingReference")} value={shipment?.TrackingReference ?? ""} disabled />
                                <MInputTextField label={t("InternalReference")} value={shipment?.InternalReference ?? ""} disabled />
                                <MInputTextField label={t("CreatedAt")} value={createdAtDisplay} disabled />
                                <MInputTextField label={t("Status")} value={trackingFirst?.Status ?? ""} disabled />

                                <MInputTextField
                                    label={t("CurrentLocation")}
                                    value={
                                        trackingFirst?.CurrentLocation
                                            ? `${trackingFirst.CurrentLocation?.Name ?? ""} (${trackingFirst.CurrentLocation?.Code ?? ""})`
                                            : ""
                                    }
                                    disabled
                                />

                                <MInputTextField
                                    label={t("Destination")}
                                    value={
                                        trackingFirst?.Destination
                                            ? `${trackingFirst.Destination?.Name ?? ""} (${trackingFirst.Destination?.Code ?? ""})`
                                            : ""
                                    }
                                    disabled
                                />

                                {scheduleField && (
                                    <MInputTextField
                                        label={scheduleField.label}
                                        value={scheduleField.value}
                                        disabled
                                    />
                                )}
                            </>
                        )}

                        {!isCreateMode && !isViewMode && (
                            <div style={{ gridColumn: "1 / -1", opacity: 0.7 }}>
                                {t("ErrorLoadingData") || "Invalid operation"}
                            </div>
                        )}
                    </div>

                    {isViewMode && !hasMovements && (
                        <div style={{ width: "100%", padding: "14px 12px", borderRadius: "8px", fontStyle: "italic" }}>
                            {t("NoTrackingMovementsForShipment") || "No Tracking Movements for this shipment."}
                        </div>
                    )}

                    {isViewMode && hasMovements && co2Tons != null && (
                        <div
                            style={{
                                width: "100%",
                                padding: "10px 12px",
                                borderRadius: "6px",
                                marginTop: "20px",
                                background: "rgba(0, 128, 0, 0.08)",
                                border: "1px solid var(--color-green)",
                            }}
                        >
                            {t("ShipmentCo2Emission") || "The actual CO2 emission of this shipment voyage is"}{" "}
                            <b>{co2Tons}</b> {t("Tons") || "tons"}.
                        </div>
                    )}

                    {isViewMode && hasMovements && (
                        <>
                            {viewTab === "movements" && (
                                <div className="tableDiv" style={{ width: "100%", display: "block", overflowX: "auto" }}>
                                    <div style={{ width: "100%", minWidth: 0 }}>
                                        <TrackingEventsTable events={eventsForTable} />
                                    </div>
                                </div>
                            )}

                            {viewTab === "containers" && hasContainers && (
                                <div className="tableDiv" style={{ width: "100%", display: "block", overflowX: "auto" }}>
                                    <div style={{ width: "100%", minWidth: 0 }}>
                                        <TrackingContainersTable trackingDetails={trackingDetails} />
                                    </div>
                                </div>
                            )}

                            {viewTab === "live" && (
                                <div style={{ width: "100%", height: 520, borderRadius: 10, overflow: "hidden", marginTop: 16 }}>
                                    <TrackingLiveMap tracking={trackingForLiveMap} />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

TrackingConfigCrud.propTypes = {
    handleTabClose: PropTypes.func,
    toastMessages: PropTypes.func,
};

/* =========================================================================
 * Local helpers
 * ========================================================================= */

/**
 * Formats a date-like value into "YYYY-MM-DD HH:mm:ss".
 *
 * Behavior:
 * - Uses the built-in Date parser.
 * - Returns the raw value as string when parsing fails.
 * - Removes milliseconds and trailing timezone marker from the ISO representation.
 *
 * @param {string|Date|null} iso - Input date value.
 * @returns {string} Formatted date string or raw input fallback.
 */
function fmtDateTime(iso) {
    if (!iso && iso !== 0) return "";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return String(iso);

        const withoutMs = d.toISOString().replace(/\.\d{3}Z$/, "Z");
        return withoutMs.replace("T", " ").replace("Z", "");
    } catch {
        return String(iso);
    }
}

/**
 * Safely converts a date-like value into epoch milliseconds.
 *
 * @param {string|Date|null} iso - Input date value.
 * @returns {number} Epoch milliseconds or NaN when parsing fails.
 */
function safeTime(iso) {
    try {
        const d = new Date(iso);
        return d.getTime();
    } catch {
        return NaN;
    }
}

/**
 * Finds the best matching event timestamp for a set of event codes.
 *
 * Selection process:
 * - Keep only events whose metadata event code matches one of the provided codes.
 * - Ignore entries without timestamp.
 * - Apply optional filters such as:
 *   - requireNotEstimated
 *   - requirePastOrNow
 * - When preferEstimated is enabled, prefer estimated candidates when available.
 * - Otherwise return the most recent candidate.
 *
 * @param {Array} events - Event collection to evaluate.
 * @param {Array<string>} codes - Accepted event codes.
 * @param {Object} opts - Selection options.
 * @param {boolean} opts.preferEstimated - Whether estimated events should be preferred.
 * @param {boolean} opts.requireNotEstimated - Whether estimated events must be excluded.
 * @param {boolean} opts.requirePastOrNow - Whether future timestamps must be excluded.
 * @returns {{iso:string,estimated:boolean}|null} Best candidate or null.
 */
function pickBestEventTimestamp(events, codes, opts = {}) {
    const list = Array.isArray(events) ? events : [];
    const set = new Set((codes || []).map((c) => String(c).toUpperCase()));

    const now = Date.now();

    const candidates = list
        .map((e) => {
            const meta = e?.Metadata ?? e?.metadata ?? {};
            const code = String(meta?.air_event ?? meta?.event_code ?? "").toUpperCase();
            const ts = e?.Timestamp;
            if (!set.has(code)) return null;
            return { iso: ts, estimated: !!e?.IsEstimated };
        })
        .filter(Boolean)
        .filter((x) => !!x.iso)
        .filter((x) => {
            if (opts.requireNotEstimated && x.estimated) return false;
            if (opts.requirePastOrNow) {
                const t = safeTime(x.iso);
                if (Number.isNaN(t)) return false;
                if (t > now) return false;
            }
            return true;
        });

    if (!candidates.length) return null;

    if (opts.preferEstimated) {
        const est = candidates.filter((c) => c.estimated);
        if (est.length) {
            return est.sort((a, b) => new Date(b.iso) - new Date(a.iso))[0];
        }
    }

    return candidates.sort((a, b) => new Date(b.iso) - new Date(a.iso))[0];
}